import { computed, Injectable, OnDestroy, signal } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { UploadedGeoImageService } from './uploaded-geo-image.service';
import {
  emptyGeoImageFilters,
  GeoImageFilters,
  GeoImageMapBounds,
  UploadedGeoImageGalleryRequest,
  UploadedGeoImageListItem,
  UploadedGeoImageMetadata,
  UploadedGeoImageRegionNode
} from './uploaded-geo-image.models';
import { boundsOf, LatLng, LatLngBounds, wktPolygonToLatLngRing } from '../utils/wkt.util';

/**
 * Shared state of the "Uploaded Geo-Images" tab. The tab is rendered in three separate regions of the
 * Satellite Images page (map, list, filter sidebar), so the list, the map, and the filters all read and
 * write this one instance instead of passing data through the page component. Every field the other two
 * regions need to react to is a signal, so an OnPush consumer that reads it (directly, or through a
 * `computed()`) redraws itself automatically — no `changed$`-style notification Subject is needed.
 *
 * Provided by the page component, so it lives exactly as long as the page does.
 */
@Injectable()
export class UploadedGeoImagesStateService implements OnDestroy {
  /** Cards per list page; the server is always asked for everything and the list pages locally. */
  readonly pageSize = 4;

  /**
   * Upper bound on rows fetched per request. The gallery is paged client-side (as the Field Photo gallery
   * is when a map viewport is set), so the map can draw every marker in view.
   */
  private static readonly FETCH_LIMIT = 1000;

  /** What the filter panel is editing. */
  readonly filters = signal<GeoImageFilters>(emptyGeoImageFilters());

  /** What the current results were fetched with; only changes on Apply / Reset. */
  readonly appliedFilters = signal<GeoImageFilters>(emptyGeoImageFilters());

  readonly items = signal<UploadedGeoImageListItem[]>([]);
  readonly total = signal(0);
  readonly loading = signal(false);
  readonly loadFailed = signal(false);
  readonly page = signal(1);
  readonly metadata = signal<UploadedGeoImageMetadata | null>(null);
  readonly selectedId = signal<string | null>(null);

  /**
   * Where the latest selection came from. Picking a card zooms the map to it and restyles its marker; picking a
   * marker or outline on the map must leave the map alone, or the rebuild would close the popup it just opened.
   */
  readonly selectionOrigin = signal<'list' | 'map'>('list');

  readonly pagedItems = computed(() => {
    const start = (this.page() - 1) * this.pageSize;
    return this.items().slice(start, start + this.pageSize);
  });

  readonly pageCount = computed(() => Math.max(1, Math.ceil(this.items().length / this.pageSize)));

  readonly selectedItem = computed(() => this.items().find(item => item.id === this.selectedId()) ?? null);

  /** Fires with the extent of the latest results after an explicit Apply / Reset / upload, so the map can show them. */
  readonly fitRequested$ = new Subject<LatLngBounds>();

  private bounds: GeoImageMapBounds | null = null;

  /** True while a load that will fit the map to its results is in flight. */
  private fitInFlight = false;
  private loadSub?: Subscription;
  private metadataSub?: Subscription;

  constructor(private readonly api: UploadedGeoImageService) {}

  ngOnDestroy(): void {
    this.loadSub?.unsubscribe();
    this.metadataSub?.unsubscribe();
    this.fitRequested$.complete();
  }

  /** First load when the tab is opened: show every image, not just those inside the map's default viewport. */
  initialize(): void {
    this.loadMetadata();
    this.load(true);
  }

  /** Apply button: fetch with the panel's filters everywhere (not just the current viewport) and show the results on the map. */
  applyFilters(): void {
    const current = this.filters();
    this.appliedFilters.set({ ...current, tagIds: [...current.tagIds] });
    this.bounds = null;
    this.page.set(1);
    this.load(true);
  }

  /** Reset button. */
  resetFilters(): void {
    this.filters.set(emptyGeoImageFilters());
    this.applyFilters();
  }

  /** Pan/zoom on the map (FR-012): refresh the list and markers to what is now in view. */
  onMapBoundsChange(bounds: GeoImageMapBounds): void {
    // A viewport reload would cancel the in-flight "show everything" load together with the fit to its results
    // (the map reports its default viewport shortly after it is created). The fit moves the map and reports
    // the real bounds afterwards, so nothing is lost by ignoring this one.
    if (this.fitInFlight) {
      return;
    }

    this.bounds = bounds;
    this.page.set(1);
    this.load(false);
  }

  /** After an upload: forget the viewport so the new images are visible wherever they are, and show them. */
  refreshAfterUpload(): void {
    this.bounds = null;
    this.page.set(1);
    this.loadMetadata();
    this.load(true);
  }

  setPage(page: number): void {
    this.page.set(Math.min(Math.max(1, page), this.pageCount()));
  }

  select(id: string | null, origin: 'list' | 'map' = 'list'): void {
    this.selectedId.set(id);
    this.selectionOrigin.set(origin);
  }

  /** Puts an edited row back in place without a round trip. */
  replaceItem(updated: UploadedGeoImageListItem): void {
    this.items.update(items => items.map(item => (item.id === updated.id ? updated : item)));
  }

  /** Drops a deleted row from the list and map, and refreshes what the region filter offers. */
  removeItem(id: string): void {
    this.items.update(items => items.filter(item => item.id !== id));
    this.total.update(total => Math.max(0, total - 1));
    if (this.selectedId() === id) {
      this.selectedId.set(null);
    }
    this.page.update(page => Math.min(page, this.pageCount()));
    this.loadMetadata();
  }

  private load(fitToResults: boolean): void {
    this.loadSub?.unsubscribe();
    this.fitInFlight = fitToResults;
    this.loading.set(true);
    this.loadFailed.set(false);

    this.loadSub = this.api.getGallery(this.buildRequest()).subscribe({
      next: response => {
        this.fitInFlight = false;
        // A pan that reveals nothing new returns the same rows. Keeping the array lets the map skip its redraw,
        // which would otherwise close the popup that the pan itself (Leaflet auto-pans to an opened popup) started.
        if (!this.sameItems(this.items(), response.items)) {
          this.items.set(response.items);
        }
        this.total.set(response.total);
        this.page.set(Math.min(this.page(), this.pageCount()));
        if (this.selectedId() && !this.items().some(item => item.id === this.selectedId())) {
          this.selectedId.set(null);
        }
        this.loading.set(false);

        if (fitToResults) {
          const extent = this.extentOf(this.items());
          if (extent) {
            this.fitRequested$.next(extent);
          }
        }
      },
      error: () => {
        this.fitInFlight = false;
        this.loading.set(false);
        this.loadFailed.set(true);
      }
    });
  }

  private loadMetadata(): void {
    this.metadataSub?.unsubscribe();
    this.metadataSub = this.api.getMetadata().subscribe({
      next: metadata => {
        this.metadata.set(metadata);
        const appliedRegionDropped = this.dropUnavailableRegion(metadata.regions);
        if (appliedRegionDropped) {
          // The results were filtered by a region that no longer exists in the gallery; show them unfiltered.
          this.load(false);
        }
      },
      error: () => {
        // The filter panel just shows no region options; the gallery itself still works.
      }
    });
  }

  private sameItems(current: UploadedGeoImageListItem[], next: UploadedGeoImageListItem[]): boolean {
    return current.length === next.length && current.every((item, index) => JSON.stringify(item) === JSON.stringify(next[index]));
  }

  /**
   * The region options only list regions that still hold an image. If the selected region vanished (its last
   * image was deleted), the tree-select clears itself silently, so the filter state has to follow or Apply would
   * keep filtering by a region the panel no longer shows. Returns whether the applied filter had to change.
   */
  private dropUnavailableRegion(regions: UploadedGeoImageRegionNode[]): boolean {
    const known = new Set<string>();
    const collect = (nodes: UploadedGeoImageRegionNode[]): void => {
      for (const node of nodes) {
        known.add(node.id);
        collect(node.children ?? []);
      }
    };
    collect(regions ?? []);

    const currentFilters = this.filters();
    if (currentFilters.regionId && !known.has(currentFilters.regionId)) {
      this.filters.set({ ...currentFilters, regionId: null });
    }

    const currentApplied = this.appliedFilters();
    if (currentApplied.regionId && !known.has(currentApplied.regionId)) {
      this.appliedFilters.set({ ...currentApplied, regionId: null });
      return true;
    }
    return false;
  }

  private buildRequest(): UploadedGeoImageGalleryRequest {
    const filters = this.appliedFilters();
    const request: UploadedGeoImageGalleryRequest = {
      regionId: filters.regionId,
      tagIds: filters.tagIds,
      captureDateFrom: filters.captureDateFrom ? this.startOfDay(filters.captureDateFrom).toISOString() : null,
      captureDateTo: filters.captureDateTo ? this.endOfDay(filters.captureDateTo).toISOString() : null,
      search: filters.search.trim() || null,
      offset: 0,
      limit: UploadedGeoImagesStateService.FETCH_LIMIT
    };

    if (this.bounds) {
      request.north = this.bounds.north;
      request.south = this.bounds.south;
      request.east = this.bounds.east;
      request.west = this.bounds.west;
    }

    return request;
  }

  private extentOf(items: UploadedGeoImageListItem[]): LatLngBounds | null {
    const points: LatLng[] = [];
    for (const item of items) {
      if (item.latitude != null && item.longitude != null) {
        points.push([item.latitude, item.longitude]);
      }
      points.push(...wktPolygonToLatLngRing(item.footprintWkt));
    }
    return boundsOf(points);
  }

  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  }

  private endOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
  }
}
